// subscription.ts

export interface SubscriptionPlan {
    id: string;    // e.g. "starter", "pro", "enterprise"
    name: string;
    level: number; // Numeric level for comparisons
    modules?: {
        [module: string]: {
            features?: {
                [feature: string]: {
                    actions: string[];
                    limits?: { [key: string]: number };
                    display?: {
                        ifLocked?: "lock" | "hide";
                        tooltip?: string;
                        showUpgradeButton?: boolean;
                    };
                };
            };
        };
    };
    pricing?: {
        monthly: number;
        yearly: number;
    };
    metadata?: {
        maxUsers?: number;
        support?: string;
        [key: string]: any;
    };
}

export const SUBSCRIPTION_DATA: SubscriptionPlan = {
    id: "starter",
    name: "Starter",
    level: 1,
    modules: {
        dashboards: {
            features: {
                dashboards: {
                    actions: ["dashboards", "view", "create", "import"],
                    limits: { maxWidgets: 5 },
                    display: {
                        ifLocked: "hide",
                        tooltip: "Upgrade for additional dashboard options.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        students: {
            features: {
                students: {
                    actions: ["students", "view", "create", "import", "export", "manage duplicates", "Bulk Edit", 'Bulk Delete'],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                },
                deals: {
                    actions: ["deals", "view", "create", "import"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more deals details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        applications: {
            features: {
                applications: {
                    actions: ["applications", "view", "create", "import", "Bulk Edit", 'Bulk Delete'],
                    limits: { maxApplications: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more application features.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        institutes: {
            features: {
                institutes: {
                    actions: ["institutes", "view", "create", "import"],
                    limits: { maxInstitutes: 20 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more institutes.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                },
                contracts: {
                    actions: ["contracts", "views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more contracts details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        courses: {
            features: {
                courses: {
                    actions: ["courses", "view", "create", "import"],
                    limits: { maxCourses: 30 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more course options.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        finances: {
            features: {
                finances: {
                    actions: ["finances", "view", "create", "import"],
                    limits: { maxFinances: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access financial tools.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        commissions: {
            features: {
                commissions: {
                    actions: ["commissions", "view", "create", "import"],
                    limits: { maxFinances: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access commissions tools.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        contracts: {
            features: {
                contracts: {
                    actions: ["contracts", "view", "create", "import"],
                    limits: { maxFinances: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access contracts tools.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more contracts details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        invoices: {
            features: {
                invoices: {
                    actions: ["invoices", "view", "create", "import"],
                    limits: { maxInvoices: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access invoice tools.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        payments: {
            features: {
                payments: {
                    actions: ["payments", "view", "create", "import"],
                    limits: { maxPayments: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access payment tools.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        disputes: {
            features: {
                disputes: {
                    actions: ["disputes", "view", "create", "import"],
                    limits: { maxDisputes: 0 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access invoice tools.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        sources: {
            features: {
                sources: {
                    actions: ["sources", "view", "create", "import"],
                    limits: { maxSources: 10 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access more sources.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        subagents: {
            features: {
                subagents: {
                    actions: ["subagents", "view", "create", "import"],
                    limits: { maxSubagents: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more sub-agent management.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                },
                contracts: {
                    actions: ["contracts", "views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more contracts details.",
                        showUpgradeButton: true
                    }
                }

            }
        },
        channels: {
            features: {
                channels: {
                    actions: ["channels", "view", "create", "import"],
                    limits: { maxChannels: 10 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for additional channel management.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                },
                contracts: {
                    actions: ["contracts", "views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more contracts details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        fields: {
            features: {
                fields: {
                    actions: ["fields", "view", "create", "import"],
                    limits: { maxFields: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more field options.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        users: {
            features: {
                users: {
                    actions: ["users", "view", "invite", "import"],
                    limits: { maxUsers: 1 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to manage more users.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        roles: {
            features: {
                roles: {
                    actions: ["roles", "view", "create", "import"],
                    limits: { maxRoles: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for advanced role management.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        teams: {
            features: {
                teams: {
                    actions: ["teams", "view", "create", "import"],
                    limits: { maxTeams: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to manage more teams.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        companies: {
            features: {
                companies: {
                    actions: ["companies", "view", "create", "import"],
                    limits: { maxCompanies: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more company options.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        banks: {
            features: {
                banks: {
                    actions: ["banks", "view", "create", "import"],
                    limits: { maxCompanies: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more banks options.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more banks details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        forms: {
            features: {
                forms: {
                    actions: ["forms", "view", "create", "import"],
                    limits: { maxForms: 10 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to access additional forms.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        products: {
            features: {
                products: {
                    actions: ["products", "view", "create", "import"],
                    limits: { maxProducts: 10 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more product options.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        templates: {
            features: {
                templates: {
                    actions: ["templates", "view", "create", "import"],
                    limits: { maxTemplates: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for additional templates.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        reports: {
            features: {
                reports: {
                    actions: ["reports", "view", "create", "import"],
                    limits: { maxReports: 5 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for advanced reporting.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        imports: {
            features: {
                imports: {
                    actions: ["imports", "view", "create", "import"],
                    limits: { maxImports: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more import capabilities.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        exports: {
            features: {
                exports: {
                    actions: ["exports", "view", "create", "export"],
                    limits: { maxExports: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more import capabilities.",
                        showUpgradeButton: true
                    }
                },
                views: {
                    actions: ["views", "create", "view", "edit"],
                    limits: { maxStudents: 100 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade to view more student details.",
                        showUpgradeButton: true
                    }
                }
            }
        },
        audits: {
            features: {
                audits: {
                    actions: ["audits", "view", "create"],
                    limits: { maxImports: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more import capabilities.",
                        showUpgradeButton: true
                    }
                },
            }
        },
        integrations: {
            features: {
                integrations: {
                    actions: ["integrations", "view", "create"],
                    limits: { maxImports: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more import capabilities.",
                        showUpgradeButton: true
                    }
                },
            }
        },
        campaigns: {
            features: {
                campaigns: {
                    actions: ["campaigns", "view", "create"],
                    limits: { maxImports: 50 },
                    display: {
                        ifLocked: "lock",
                        tooltip: "Upgrade for more import capabilities.",
                        showUpgradeButton: true
                    }
                },
            }
        }
    },
    pricing: {
        monthly: 0,
        yearly: 0
    },
    metadata: {
        maxUsers: 1,
        support: "community support"
    }
};
